import type {
  EvaluateExpressionFieldResult,
  ExpressionFormValues,
  ExpressionVariablesValues,
  ExpressionVariableValue,
} from "./expression-field.type";
import type {
  SchemaExpressionNode,
  SchemaFieldExpression,
} from "@/entities/schema";

import {
  getVariablesModelsFromAST,
  safeEvaluateExpression,
} from "@stelar/expression-engine";

import { isValidNumber } from "@/utilities/number";
import { isValidObject } from "@/utilities/object";

export const evaluateExpressionField = (
  schema: SchemaFieldExpression,
  formValues?: unknown
): EvaluateExpressionFieldResult => {
  if (!isValidFormValues(formValues)) {
    return {};
  }

  if (!formValuesAreDefined(schema.expression, formValues)) {
    return {};
  }

  try {
    const { precision } = schema;
    const ast = schema.expression;
    const values = getExpressionValues(ast, formValues);
    const result = safeEvaluateExpression(ast, values, { precision });
    return { result };
  } catch (error) {
    return { error };
  }
};

export const formValuesAreDefined = (
  expression: SchemaExpressionNode,
  formValues?: ExpressionFormValues
): boolean => {
  if (!formValues) return false;

  let expressionModels: string[] = [];
  const formModels = Object.keys(formValues);

  try {
    expressionModels = getVariablesModelsFromAST(expression);
  } catch {
    /* nothing */
  }

  return expressionModels.every((model) => {
    // If model field does not exist in form, return true and
    // let expression-engine return a `MISSING_VALUES` error
    if (!formModels.includes(model)) return true;

    // Otherwise verify if model field has a valid value before
    // asking expression-engine to evaluate the expression
    return isValidFormValue(formValues[model]);
  });
};

export const getExpressionValues = (
  expression: SchemaExpressionNode,
  formValues: ExpressionFormValues
) => {
  const values: ExpressionVariablesValues = {};
  let models: string[] = [];

  try {
    models = getVariablesModelsFromAST(expression);
  } catch {
    return values;
  }

  models.forEach((model) => {
    const value = formValues[model];
    if (isValidFormValue(value)) values[model] = value;
  });

  return values;
};

const isValidFormValues = (
  formValues?: unknown
): formValues is ExpressionFormValues => {
  return isValidObject(formValues);
};

const isValidFormValue = (
  formValue?: unknown
): formValue is ExpressionVariableValue => {
  if (isValidNumber(formValue)) return true;
  if (typeof formValue === "string" && formValue !== "") return true;
  return false;
};
